import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { vehicleAPI } from '../../services/api';
import { Button, Row, Col, Alert, Spinner, Form } from 'react-bootstrap';
import { FiSave, FiArrowLeft, FiLink } from 'react-icons/fi';

const VehicleFormPage: React.FC = () => {
  const { id }     = useParams();
  const navigate   = useNavigate();
  const isEdit     = !!id;

  const [loading, setLoading]   = useState(isEdit);
  const [saving, setSaving]     = useState(false);
  const [error, setError]       = useState<string | null>(null);
  const [success, setSuccess]   = useState<string | null>(null);

  const [formData, setFormData] = useState({
    plateNumber: '', vehicleMake: '', vehicleModel: '',
    year: '', color: '', isActive: true,
    licenseExpiryDate: '', insuranceExpiryDate: '',
  });

  const [licenseImage, setLicenseImage]           = useState<File | null>(null);
  const [insuranceImage, setInsuranceImage]       = useState<File | null>(null);
  const [currentLicenseUrl, setCurrentLicenseUrl] = useState<string | null>(null);
  const [currentInsuranceUrl, setCurrentInsuranceUrl] = useState<string | null>(null);

  useEffect(() => { if (isEdit) fetchVehicle(); }, [id]);

  const fetchVehicle = async () => {
    try {
      const response = await vehicleAPI.getVehicle(parseInt(id!));
      const v = response.data;
      setFormData({
        plateNumber: v.plateNumber,
        vehicleMake: v.vehicleMake || '',
        vehicleModel: v.vehicleModel || '',
        year: v.year?.toString() || '',
        color: v.color || '',
        isActive: v.isActive,
        licenseExpiryDate: v.licenseExpiryDate || '',
        insuranceExpiryDate: v.insuranceExpiryDate || '',
      });
      setCurrentLicenseUrl(v.licenseImageUrl);
      setCurrentInsuranceUrl(v.insuranceImageUrl);
    } catch {
      setError('Failed to load vehicle');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const data = {
        plateNumber:         formData.plateNumber,
        vehicleMake:         formData.vehicleMake         || undefined,
        vehicleModel:        formData.vehicleModel        || undefined,
        year:                formData.year ? parseInt(formData.year) : undefined,
        color:               formData.color               || undefined,
        isActive:            formData.isActive,
        licenseExpiryDate:   formData.licenseExpiryDate   || undefined,
        insuranceExpiryDate: formData.insuranceExpiryDate || undefined,
      };

      let vehicleId: number;
      if (isEdit) {
        await vehicleAPI.updateVehicle(parseInt(id!), data);
        vehicleId = parseInt(id!);
        setSuccess('Vehicle updated successfully');
      } else {
        const response = await vehicleAPI.createVehicle(data);
        vehicleId = response.data.id;
        setSuccess('Vehicle created successfully');
      }

      if (licenseImage)   await vehicleAPI.uploadLicenseImage(vehicleId, licenseImage);
      if (insuranceImage) await vehicleAPI.uploadInsuranceImage(vehicleId, insuranceImage);

      if (!isEdit) {
        navigate(`/admin/vehicles/${vehicleId}`);
      } else {
        fetchVehicle();
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to save vehicle');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 300 }}>
        <Spinner animation="border" />
      </div>
    );
  }

  return (
    <>
      <div className="page-header">
        <h2>{isEdit ? `Edit: ${formData.plateNumber}` : 'Add New Vehicle'}</h2>
        <Button variant="outline-secondary" className="d-flex align-items-center gap-2"
          onClick={() => navigate('/admin/vehicles')}>
          <FiArrowLeft /> Back
        </Button>
      </div>

      {error   && <Alert variant="danger">{error}</Alert>}
      {success && <Alert variant="success">{success}</Alert>}

      <div className="section-card">
        <div className="section-card-header">
          <span className="section-card-title">Vehicle Details</span>
        </div>
        <div className="section-card-body">
          <Form onSubmit={handleSubmit}>
            <Row className="g-3">
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Plate Number *</Form.Label>
                  <Form.Control
                    type="text" name="plateNumber"
                    value={formData.plateNumber} onChange={handleChange}
                    required disabled={isEdit} placeholder="e.g., ABC1234"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Vehicle Make</Form.Label>
                  <Form.Control
                    type="text" name="vehicleMake"
                    value={formData.vehicleMake} onChange={handleChange}
                    placeholder="e.g., Toyota"
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group>
                  <Form.Label>Vehicle Model</Form.Label>
                  <Form.Control
                    type="text" name="vehicleModel"
                    value={formData.vehicleModel} onChange={handleChange}
                    placeholder="e.g., Camry"
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group>
                  <Form.Label>Year</Form.Label>
                  <Form.Control
                    type="number" name="year"
                    value={formData.year} onChange={handleChange}
                    placeholder="e.g., 2023"
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group>
                  <Form.Label>Color</Form.Label>
                  <Form.Control
                    type="text" name="color"
                    value={formData.color} onChange={handleChange}
                    placeholder="e.g., White"
                  />
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group>
                  <Form.Label>License Expiry Date</Form.Label>
                  <Form.Control
                    type="date" name="licenseExpiryDate"
                    value={formData.licenseExpiryDate} onChange={handleChange}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Insurance Expiry Date</Form.Label>
                  <Form.Control
                    type="date" name="insuranceExpiryDate"
                    value={formData.insuranceExpiryDate} onChange={handleChange}
                  />
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group>
                  <Form.Label>License PDF</Form.Label>
                  {currentLicenseUrl && (
                    <div className="mb-1" style={{ fontSize: '0.78rem', color: '#059669' }}>
                      ✓ {currentLicenseUrl.split('/').pop()}
                    </div>
                  )}
                  <Form.Control
                    type="file" accept=".pdf,application/pdf"
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      setLicenseImage(e.target.files?.[0] || null)}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Insurance PDF</Form.Label>
                  {currentInsuranceUrl && (
                    <div className="mb-1" style={{ fontSize: '0.78rem', color: '#059669' }}>
                      ✓ {currentInsuranceUrl.split('/').pop()}
                    </div>
                  )}
                  <Form.Control
                    type="file" accept=".pdf,application/pdf"
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      setInsuranceImage(e.target.files?.[0] || null)}
                  />
                </Form.Group>
              </Col>

              {isEdit && (
                <Col xs={12}>
                  <Form.Check
                    type="switch" name="isActive" id="isActive"
                    label="Active" checked={formData.isActive}
                    onChange={handleChange}
                  />
                </Col>
              )}
            </Row>

            <div className="d-flex gap-2 mt-4">
              <Button variant="primary" type="submit" disabled={saving}
                className="d-flex align-items-center gap-2">
                <FiSave />
                {saving ? 'Saving…' : (isEdit ? 'Update Vehicle' : 'Create Vehicle')}
              </Button>
              <Button variant="outline-secondary" onClick={() => navigate('/admin/vehicles')}>
                Cancel
              </Button>
            </div>
          </Form>
        </div>
      </div>

      {isEdit && (
        <div className="section-card mt-3" style={{ background: '#f8fafc' }}>
          <div className="section-card-body">
            <div className="d-flex align-items-center gap-2 mb-1" style={{ fontWeight: 600, fontSize: '0.85rem' }}>
              <FiLink style={{ color: '#16a34a' }} /> NFC Card URL
            </div>
            <code style={{
              display: 'block', background: '#fff', padding: '0.6rem 0.85rem',
              borderRadius: 8, border: '1px solid #e2e8f0', fontSize: '0.82rem',
              color: '#1e293b', wordBreak: 'break-all',
            }}>
              {window.location.origin}/car?plate={formData.plateNumber}
            </code>
          </div>
        </div>
      )}
    </>
  );
};

export default VehicleFormPage;
